import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Save, Plus, ClipboardList, Hammer, Phone, Mail, MapPin, User, Star, ShoppingCart, FileText, CalendarClock, Trash2, ExternalLink, Truck, Pencil, Paperclip, Upload, Eye, Download, Gavel } from 'lucide-react';
import { api } from '../../core/apiClient';
import apiClient from '../../core/apiClient';
import { useAuth } from '../../auth/AuthContext';
import { useCompositeCodes, compositeCodeLabel } from '../../hooks/useCompositeCodes';
import { CollapsibleCard } from '../../components/ui/collapsible-card';
import DocumentHeader, { HeaderBadge } from '../../components/DocumentHeader';
import StatusSelect from '../../components/StatusSelect';

interface Client { id: string; name: string; code: string; cif?: string; }
interface Brand { id: string; code: string; name: string; }
interface Budget { id: string; budgetNumber: string; estat: string; total: number; baseImposable: number; projecte?: string; }
interface EcDocumentRow { id: string; name: string; fileName: string; fileSize: number; mimeType?: string; createdAt: string; description?: string; }
interface TenderInfo {
  id: string; location?: string; poblacio?: string; totalAmount?: number;
  adjudicationAmount?: number;
  tenderType?: string; noticeDate?: string; deadline?: string; status: string;
}

const TENDER_STATUS_LABEL: Record<string, string> = {
  NO_PRESENTAT: 'En estudi', PRESENTAT_PENDENT: 'Licitat',
  PRESENTAT_PERDUT: 'Desestimat', GUANYAT: 'Adjudicat',
};

interface ClientDetailInfo {
  id: string;
  name: string;
  cif?: string;
  phone?: string;
  email?: string;
  website?: string;
  address?: string;
  city?: string;
  postalCode?: string;
}

interface ClientContact {
  id: string;
  firstName: string;
  lastName: string;
  position?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  isPrimary: boolean;
}

interface EcForm {
  clientId: string;
  brandId: string;
  name: string;
  etiquetaExpedient: string;
  clientExpedientRef: string;
  description: string;
  location: string;
  responsableProjecte: string;
  responsableEmpresa: string;
  compositeCodeId: string; // Tipus de producte / Família (id de composite_codes)
  compositeSubfamilyId: string; // Subfamília (id de composite_subfamilies)
  compositeProductId: string; // Producte (id de composite_products)
}

const EMPTY_FORM = (): EcForm => ({ clientId: '', brandId: '', name: '', etiquetaExpedient: '', clientExpedientRef: '', description: '', location: '', responsableProjecte: '', responsableEmpresa: '', compositeCodeId: '', compositeSubfamilyId: '', compositeProductId: '' });

const onlyAlphanumeric = (v: string) => v.replace(/[^a-zA-Z0-9]/g, '');

const googleMapsUrl = (location: string) => `https://www.google.com/maps?q=${encodeURIComponent(location.trim())}`;

const ESTAT_LABEL: Record<string, string> = {
  ESBORRANY: 'Obert', ENVIAT: 'Licitat', DESESTIMAT: 'Desestimat', ADJUDICAT: 'Proposat', FACTIBLE: 'Factible',
  ACCEPTAT: 'Comanda', REBUTJAT: 'Descartat', LLIURAT: 'Lliurat', FACTURAT: 'Facturat',
};

// Colors per estat de l'Expedient Comercial (EC). El nom es carrega del
// catàleg unificat /admin/estats-crm?module=ec; el color és fix per codi.
// Mateixos colors que les etiquetes d'estat de Licitacions (Licitacions.tsx: STATUS_CONFIG)
// pels 3 estats comuns: Licitat=blau (primary), Desestimat=neutre (outline), Proposat=verd (success).
const EC_ESTAT_COLOR: Record<string, string> = {
  LICITAT:    'bg-blue-100 text-blue-700',
  DESESTIMAT: 'bg-slate-100 text-slate-700',
  ADJUDICAT:  'bg-green-100 text-green-700',
  FACTIBLE:   'bg-amber-100 text-amber-700',
  DESCARTAT:  'bg-gray-100 text-gray-600',
  ACCEPTAT:   'bg-emerald-100 text-emerald-700',
  LLIURAT:    'bg-purple-100 text-purple-700',
  TANCAT:     'bg-slate-200 text-slate-700',
};

// Estats en els que es permet crear un pressupost nou sobre l'EC. Inclou els
// previs a l'aprovació i, a més, ACCEPTAT/LLIURAT/TANCAT — un cop l'EC ja te
// Expedient de Taller, el client pot demanar mes endavant una ampliació o
// una instal·lació nova, que es pressuposta i s'afegeix al mateix EP sense
// obrir-ne un de nou (veure ampliarEP a expedient-comercial.service.ts).
const EC_ESTATS_PERMETEN_NOU_PRESSUPOST = ['PRESENTAT_PENDENT', 'DESESTIMAT', 'ADJUDICAT', 'FACTIBLE', 'PRESENTAT_PERDUT', 'ACCEPTAT', 'LLIURAT', 'FACTURAT'];

interface CompanyUser { id: string; firstName: string; lastName: string; role?: string | null; }

// Rols del catàleg admin/roles que poden fer seguiment comercial d'un EC.
const SEGUIMENT_ROLE_CODES = ['ADMIN', 'GERENTE', 'OFICINA', 'CONTABLE'];
const DIACRITICS_RE = new RegExp('[' + String.fromCharCode(0x0300) + '-' + String.fromCharCode(0x036f) + ']', 'g');
const normalizeRole = (r?: string | null) => (r ?? '').toUpperCase().normalize('NFD').replace(DIACRITICS_RE, '');
const isSeguimentRole = (role?: string | null) => SEGUIMENT_ROLE_CODES.includes(normalizeRole(role));

interface Seguiment {
  id: string;
  userId: string;
  user?: { firstName: string; lastName: string };
  dataComentari: string;
  canal: string;
  interlocutor?: string;
  comentari: string;
  dataProperAvis?: string | null;
  valoracio?: string | null;
}

const CANAL_LABEL: Record<string, string> = { TELEFONIC: 'Telefònic', CORREU: 'Correu', ALTRES: 'Altres' };

const VALORACIO_LABEL: Record<string, string> = {
  MOLT_POSITIVA: 'Molt positiva',
  POSITIVA: 'Positiva',
  NEUTRA: 'Neutra',
  NEGATIVA: 'Negativa',
  MOLT_NEGATIVA: 'Molt negativa',
};

const VALORACIO_COLOR: Record<string, string> = {
  MOLT_POSITIVA: 'bg-green-100 text-green-700',
  POSITIVA: 'bg-lime-100 text-lime-700',
  NEUTRA: 'bg-gray-100 text-gray-600',
  NEGATIVA: 'bg-amber-100 text-amber-700',
  MOLT_NEGATIVA: 'bg-red-100 text-red-700',
};

const todayIso = () => new Date().toISOString().slice(0, 10);

interface SeguimentForm {
  userId: string;
  dataComentari: string;
  canal: string;
  interlocutor: string;
  comentari: string;
  dataProperAvis: string;
  valoracio: string;
}

const EMPTY_SEGUIMENT_FORM = (userId: string): SeguimentForm => ({
  userId,
  dataComentari: todayIso(),
  canal: 'CORREU',
  interlocutor: '',
  comentari: '',
  dataProperAvis: '',
  valoracio: '',
});

export default function ExpedientComercialForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const clientIdParam = searchParams.get('clientId') ?? '';
  const isNew = !id || id === 'new';
  const { user: authUser } = useAuth();

  const [form, setForm] = useState<EcForm>(EMPTY_FORM());
  const [ecNumber, setEcNumber] = useState('(auto)');
  const [estat, setEstat] = useState('PRESENTAT_PENDENT');
  const [projectNumber, setProjectNumber] = useState<string | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [changingEstat, setChangingEstat] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [clientSearch, setClientSearch] = useState('');
  const [clientOpen, setClientOpen] = useState(false);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [tender, setTender] = useState<TenderInfo | null>(null);
  const [documents, setDocuments] = useState<EcDocumentRow[]>([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docsDragOver, setDocsDragOver] = useState(false);
  const [editingDocId, setEditingDocId] = useState<string | null>(null);
  const [editingDocDescription, setEditingDocDescription] = useState('');
  const [estatsOpts, setEstatsOpts] = useState<{ id: number; code: string; name: string }[]>([]);
  const compositeCodes = useCompositeCodes();
  const [compositeSubfamilies, setCompositeSubfamilies] = useState<{ id: number; code: string; name: string; compositeCodeId: number; isActive: number | boolean }[]>([]);
  const [compositeProducts, setCompositeProducts] = useState<{ id: number; code: string; name: string; subfamilyId: number; isActive: number | boolean }[]>([]);
  const [clientDetail, setClientDetail] = useState<ClientDetailInfo | null>(null);
  const [contacts, setContacts] = useState<ClientContact[]>([]);

  const [companyUsers, setCompanyUsers] = useState<CompanyUser[]>([]);
  const [seguiments, setSeguiments] = useState<Seguiment[]>([]);
  const [showSeguimentForm, setShowSeguimentForm] = useState(false);
  const [seguimentForm, setSeguimentForm] = useState<SeguimentForm>(EMPTY_SEGUIMENT_FORM(authUser?.id ?? ''));
  const [savingSeguiment, setSavingSeguiment] = useState(false);
  const [editingSeguimentId, setEditingSeguimentId] = useState<string | null>(null);
  // TIC vermell a la targeta "Seguiment Comercial" quan hi ha algun
  // seguiment (de qualsevol usuari) que aquest usuari encara no ha vist.
  const [hasUnseenSeguiment, setHasUnseenSeguiment] = useState(false);


  const loadSeguiments = useCallback(async () => {
    if (!id || isNew) return;
    try {
      const res = await api.get<{ success: boolean; data: Seguiment[] }>('/expedients-comercials-seguiments', { ecId: id });
      setSeguiments(res.data ?? []);
    } catch {
      setSeguiments([]);
    }
  }, [id, isNew]);

  const loadUnseenSeguiment = useCallback(async () => {
    if (!id || isNew) return;
    try {
      const res = await api.get<{ success: boolean; data: { hasUnseen: boolean } }>('/expedients-comercials-seguiments/unseen', { ecId: id });
      setHasUnseenSeguiment(!!res.data?.hasUnseen);
    } catch {
      setHasUnseenSeguiment(false);
    }
  }, [id, isNew]);

  const handleSeguimentCardToggle = (open: boolean) => {
    if (open && hasUnseenSeguiment && id) {
      setHasUnseenSeguiment(false);
      api.post('/expedients-comercials-seguiments/mark-viewed', { ecId: id }).catch(() => {});
    }
  };

  const load = useCallback(async () => {
    try {
      const empty = { success: true, data: [] };
      const [clientsRes, brandsRes, estatsRes, usersRes, subRes, prodRes] = await Promise.all([
        api.get<{ success: boolean; data: Client[] }>('/clients', { limit: 500, isActive: 1 }),
        api.get<{ success: boolean; data: Brand[] }>('/brands').catch(() => empty),
        api.get<{ success: boolean; data: any[] }>('/admin/estats-crm', { module: 'ec' }).catch(() => empty),
        api.get<{ success: boolean; data: CompanyUser[] }>('/auth/users').catch(() => empty),
        api.get<{ success: boolean; data: typeof compositeSubfamilies }>('/catalog/composite-subfamilies').catch(() => empty),
        api.get<{ success: boolean; data: typeof compositeProducts }>('/catalog/composite-products').catch(() => empty),
      ]);
      const clientsList = clientsRes.data ?? [];
      setClients(clientsList);
      setBrands(brandsRes.data ?? []);
      setEstatsOpts((estatsRes.data ?? []).filter((e: any) => e.isActive));
      setCompanyUsers(usersRes.data ?? []);
      setCompositeSubfamilies((subRes.data ?? []).filter((s) => Number(s.isActive) === 1));
      setCompositeProducts((prodRes.data ?? []).filter((p) => Number(p.isActive) === 1));

      if (!isNew && id) {
        const res = await api.get<{ success: boolean; data: any }>(`/expedients-comercials/${id}`);
        const ec = res.data;
        setEcNumber(ec.ecNumber);
        setEstat(ec.estat);
        setProjectNumber(ec.project?.projectNumber ?? null);
        setForm({
          clientId: ec.clientId ?? '',
          brandId: ec.brandId ?? '',
          name: ec.name ?? '',
          etiquetaExpedient: ec.etiquetaExpedient ?? '',
          clientExpedientRef: ec.clientExpedientRef ?? '',
          description: ec.description ?? '',
          location: ec.location ?? '',
          responsableProjecte: ec.responsableProjecte ?? '',
          responsableEmpresa: ec.responsableEmpresa ?? '',
          compositeCodeId: ec.compositeCodeId != null ? String(ec.compositeCodeId) : '',
          compositeSubfamilyId: ec.compositeSubfamilyId != null ? String(ec.compositeSubfamilyId) : '',
          compositeProductId: ec.compositeProductId != null ? String(ec.compositeProductId) : '',
        });
        setBudgets(ec.budgets ?? []);
        setTender(ec.tender ?? null);
        setClientSearch(ec.client?.name ?? clientsList.find((c) => c.id === ec.clientId)?.name ?? '');
      } else if (clientIdParam) {
        setForm((f) => ({ ...f, clientId: clientIdParam }));
        setClientSearch(clientsList.find((c) => c.id === clientIdParam)?.name ?? '');
      }
    } finally {
      setLoading(false);
    }
  }, [id, isNew, clientIdParam]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSeguiments(); }, [loadSeguiments]);
  useEffect(() => { loadUnseenSeguiment(); }, [loadUnseenSeguiment]);

  // ── Documents adjunts a la fitxa de l'Expedient Comercial ────────────────

  const fetchDocuments = useCallback(async () => {
    if (!id || isNew) return;
    try {
      const res = await api.get<{ success: boolean; data: EcDocumentRow[] }>(`/expedients-comercials/${id}/documents`);
      setDocuments(res.data ?? []);
    } catch { /* no bloquejar la resta del formulari si falla el llistat */ }
  }, [id, isNew]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const handleUploadDocument = async (file: File) => {
    if (!id || !file) return;
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', file.name);
      await apiClient.post(`/expedients-comercials/${id}/documents`, fd, { headers: { 'Content-Type': undefined } });
      fetchDocuments();
    } catch (err: any) {
      alert('Error pujant el document: ' + (err?.response?.data?.message ?? err?.message));
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDocumentsDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setDocsDragOver(false);
    Array.from(e.dataTransfer.files ?? []).forEach((f) => handleUploadDocument(f));
  };

  const startEditDocDescription = (doc: EcDocumentRow) => {
    setEditingDocId(doc.id);
    setEditingDocDescription(doc.description ?? '');
  };

  const handleSaveDocDescription = async (docId: string) => {
    if (!id) return;
    try {
      await api.patch(`/expedients-comercials/${id}/documents/${docId}`, { description: editingDocDescription });
      setDocuments((prev) => prev.map((d) => (d.id === docId ? { ...d, description: editingDocDescription.trim() || undefined } : d)));
    } catch {
      alert('Error desant la descripció');
    } finally {
      setEditingDocId(null);
    }
  };

  const handleDeleteDocument = async (docId: string) => {
    if (!id) return;
    if (!confirm('Eliminar aquest document?')) return;
    try {
      await api.delete(`/expedients-comercials/${id}/documents/${docId}`);
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
    } catch { alert('Error eliminant el document'); }
  };

  // Les rutes de documents requereixen el token d'autenticació (Authorization
  // header), que un <a href> o window.open directe no envia — es baixa com a
  // blob i s'obre/descarrega des d'aquí.
  const handleOpenDocument = async (doc: EcDocumentRow, download: boolean) => {
    try {
      const res = await apiClient.get(`/expedients-comercials/${id}/documents/${doc.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: doc.mimeType || 'application/octet-stream' }));
      if (download) {
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.name;
        a.click();
      } else {
        window.open(url, '_blank');
      }
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      alert('Error obrint el document');
    }
  };

  const fmtFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  useEffect(() => {
    if (!form.clientId) { setClientDetail(null); setContacts([]); return; }
    api.get<{ success: boolean; data: any }>(`/clients/${form.clientId}`)
      .then((res) => setClientDetail(res.data ?? null))
      .catch(() => setClientDetail(null));
    api.get<{ success: boolean; data: ClientContact[] }>(`/clients/${form.clientId}/contacts`)
      .then((res) => setContacts(res.data ?? []))
      .catch(() => setContacts([]));
  }, [form.clientId]);

  const handleSave = async () => {
    if (!form.clientId) { alert('Cal seleccionar un client'); return; }
    if (!form.name.trim()) { alert('Cal indicar un nom per l\'expedient'); return; }
    setSaving(true);
    try {
      if (isNew) {
        await api.post<{ success: boolean; data: any }>('/expedients-comercials', {
          clientId: form.clientId,
          brandId: form.brandId || null,
          name: form.name,
          etiquetaExpedient: form.etiquetaExpedient || null,
          clientExpedientRef: form.clientExpedientRef || null,
          description: form.description,
          location: form.location || null,
          responsableProjecte: form.responsableProjecte || null,
          responsableEmpresa: form.responsableEmpresa || null,
          compositeCodeId: form.compositeCodeId ? Number(form.compositeCodeId) : null,
          compositeSubfamilyId: form.compositeSubfamilyId ? Number(form.compositeSubfamilyId) : null,
          compositeProductId: form.compositeProductId ? Number(form.compositeProductId) : null,
        });
      } else {
        await api.put(`/expedients-comercials/${id}`, {
          brandId: form.brandId || null,
          name: form.name,
          etiquetaExpedient: form.etiquetaExpedient || null,
          clientExpedientRef: form.clientExpedientRef || null,
          description: form.description,
          location: form.location || null,
          responsableProjecte: form.responsableProjecte || null,
          responsableEmpresa: form.responsableEmpresa || null,
          compositeCodeId: form.compositeCodeId ? Number(form.compositeCodeId) : null,
          compositeSubfamilyId: form.compositeSubfamilyId ? Number(form.compositeSubfamilyId) : null,
          compositeProductId: form.compositeProductId ? Number(form.compositeProductId) : null,
        });
      }
      sessionStorage.setItem('erp:crm:activeTab', JSON.stringify('expedients'));
      navigate('/crm');
    } catch (err: any) {
      alert('Error desant: ' + (err?.response?.data?.message ?? err?.message));
    } finally {
      setSaving(false);
    }
  };

  const handleEstatChange = async (nou: string) => {
    if (!id || nou === estat) return;
    setChangingEstat(true);
    try {
      await api.put(`/expedients-comercials/${id}`, { estat: nou });
      setEstat(nou);
    } catch (err: any) {
      alert('Error canviant l\'estat: ' + (err?.response?.data?.message ?? err?.message));
    } finally {
      setChangingEstat(false);
    }
  };

  const handleOpenSeguimentForm = () => {
    setEditingSeguimentId(null);
    setSeguimentForm(EMPTY_SEGUIMENT_FORM(authUser?.id ?? ''));
    setShowSeguimentForm(true);
  };

  const handleEditSeguiment = (s: Seguiment) => {
    setEditingSeguimentId(s.id);
    setSeguimentForm({
      userId: s.userId ?? '',
      dataComentari: s.dataComentari ? s.dataComentari.slice(0, 10) : todayIso(),
      canal: s.canal,
      interlocutor: s.interlocutor ?? '',
      comentari: s.comentari,
      dataProperAvis: s.dataProperAvis ? s.dataProperAvis.slice(0, 10) : '',
      valoracio: s.valoracio ?? '',
    });
    setShowSeguimentForm(true);
  };

  const handleSaveSeguiment = async () => {
    if (!id) return;
    if (!seguimentForm.comentari.trim()) { alert('Cal indicar un comentari'); return; }
    setSavingSeguiment(true);
    try {
      const payload = {
        ecId: id,
        userId: seguimentForm.userId || undefined,
        dataComentari: seguimentForm.dataComentari,
        canal: seguimentForm.canal,
        interlocutor: seguimentForm.interlocutor || undefined,
        comentari: seguimentForm.comentari,
        dataProperAvis: seguimentForm.dataProperAvis || null,
        valoracio: seguimentForm.valoracio || null,
      };
      if (editingSeguimentId) {
        await api.put(`/expedients-comercials-seguiments/${editingSeguimentId}`, payload);
      } else {
        await api.post('/expedients-comercials-seguiments', payload);
      }
      setShowSeguimentForm(false);
      setEditingSeguimentId(null);
      loadSeguiments();
      loadUnseenSeguiment();
    } catch (err: any) {
      alert('Error desant el seguiment: ' + (err?.response?.data?.message ?? err?.message));
    } finally {
      setSavingSeguiment(false);
    }
  };

  const handleDeleteSeguiment = async (seguimentId: string) => {
    if (!confirm('Eliminar aquest seguiment?')) return;
    try {
      await api.delete(`/expedients-comercials-seguiments/${seguimentId}`);
      loadSeguiments();
      loadUnseenSeguiment();
    } catch (err: any) {
      alert('Error eliminant el seguiment: ' + (err?.response?.data?.message ?? err?.message));
    }
  };

  if (loading) return <div className="p-8 text-muted-foreground text-xs">Carregant...</div>;

  // Si cap usuari coincideix amb els rols esperats (ex: noms de rol inesperats
  // a BD), es mostren tots els usuaris de l'empresa per no bloquejar la funcionalitat.
  const byRoleUsers = companyUsers.filter((u) => isSeguimentRole(u.role));
  const seguimentUserOptions = byRoleUsers.length > 0 ? byRoleUsers : companyUsers;

  const inputCls = 'w-full px-3 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';
  const labelCls = 'block text-xs font-medium text-muted-foreground mb-1';

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <DocumentHeader
        title={isNew ? 'Nou Expedient Comercial' : ecNumber}
        infoBadges={form.brandId && (
          <HeaderBadge tone="brand">{brands.find((b) => b.id === form.brandId)?.name ?? form.brandId}</HeaderBadge>
        )}
        status={!isNew && (
          <StatusSelect
            value={estat}
            options={estatsOpts.map((e) => ({ code: e.code, name: e.name, className: EC_ESTAT_COLOR[e.code] }))}
            fallbackLabel={estat}
            onChange={handleEstatChange}
          />
        )}
        actions={<>
          {projectNumber && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded text-xs font-medium">
              <Hammer size={14} /> Expedient de Taller: {projectNumber}
            </span>
          )}
          <button
            onClick={() => { sessionStorage.setItem('erp:crm:activeTab', JSON.stringify('expedients')); navigate('/crm'); }}
            className="px-4 py-2 border border-gray-200 rounded-lg text-xs text-gray-500 hover:bg-gray-50"
          >
            Cancel·lar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            <Save size={14} /> {saving ? 'Desant...' : 'Desar'}
          </button>
        </>}
      />

      <div className="flex-1 overflow-y-auto p-6">
      <div className="grid grid-cols-2 gap-6">

      <div className="space-y-6">
        <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
          <div className="grid grid-cols-[6fr_2fr] gap-4">
            <div className="relative">
              <label className={labelCls}>Client *</label>
              <div className="flex items-center gap-2">
                {form.clientId && (
                  <a
                    href={`/crm/clients/${form.clientId}/edit`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Editar fitxa del client"
                    className="text-gray-400 hover:text-blue-600 shrink-0"
                  >
                    <ExternalLink size={15} />
                  </a>
                )}
                <div className="relative flex-1">
                  <input
                    type="text"
                    className={`${inputCls} disabled:bg-gray-100 disabled:text-gray-500`}
                    placeholder="Cerca per nom o CIF..."
                    value={clientSearch}
                    disabled={!isNew}
                    onChange={(e) => { setClientSearch(e.target.value); setClientOpen(true); }}
                    onFocus={() => setClientOpen(true)}
                    onBlur={() => setTimeout(() => setClientOpen(false), 150)}
                    autoComplete="off"
                  />
                  {clientOpen && isNew && clientSearch.length >= 1 && (() => {
                    const hits = clients.filter((c) =>
                      c.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
                      (c.cif ?? '').toLowerCase().includes(clientSearch.toLowerCase())
                    ).slice(0, 10);
                    return hits.length > 0 ? (
                      <ul className="absolute z-50 left-0 right-0 top-full mt-0.5 bg-white border border-gray-200 rounded shadow-lg max-h-56 overflow-y-auto text-sm">
                        {hits.map((c) => (
                          <li key={c.id}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setForm((f) => ({ ...f, clientId: c.id }));
                              setClientSearch(c.name);
                              setClientOpen(false);
                            }}
                            className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-0">
                            <span className="font-medium">{c.name}</span>
                            {c.cif && <span className="text-gray-400 text-xs ml-2">{c.cif}</span>}
                          </li>
                        ))}
                      </ul>
                    ) : null;
                  })()}
                  {form.clientId && !clientOpen && isNew && (
                    <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
                      onClick={() => { setForm((f) => ({ ...f, clientId: '' })); setClientSearch(''); }}>
                      ✕
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div>
              <label className={labelCls}>Marca</label>
              <select
                value={form.brandId}
                onChange={(e) => setForm((f) => ({ ...f, brandId: e.target.value }))}
                className={`${inputCls} bg-white`}
              >
                <option value="">— Sense marca —</option>
                {brands.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-[5fr_2fr_3fr] gap-4">
            <div>
              <label className={labelCls}>Nom de l'expedient *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Ex: Instal·lació cuina — Client X"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Etiqueta Expedient</label>
              <input
                type="text"
                value={form.etiquetaExpedient}
                onChange={(e) => setForm((f) => ({ ...f, etiquetaExpedient: e.target.value }))}
                placeholder="Ex: Cuina Client X"
                maxLength={50}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Expedient del Client</label>
              <input
                type="text"
                value={form.clientExpedientRef}
                onChange={(e) => setForm((f) => ({ ...f, clientExpedientRef: onlyAlphanumeric(e.target.value) }))}
                placeholder="Ex: AB1234"
                className={inputCls}
              />
            </div>
          </div>
          {/* Tipus de producte / Subfamília / Producte — els 3 primers
              nivells de l'Arbre d'elements, en cascada, a la mateixa línia. */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>Tipus de producte</label>
              <select
                value={form.compositeCodeId}
                onChange={(e) => setForm((f) => ({
                  ...f,
                  compositeCodeId: e.target.value,
                  compositeSubfamilyId: '',
                  compositeProductId: '',
                }))}
                className={`${inputCls} bg-white`}
              >
                <option value="">— Selecciona —</option>
                {compositeCodes.optionsFor(form.compositeCodeId).map((cc) => (
                  <option key={cc.id} value={String(cc.id)}>{compositeCodeLabel(cc)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Subfamília</label>
              <select
                value={form.compositeSubfamilyId}
                disabled={!form.compositeCodeId}
                onChange={(e) => setForm((f) => ({ ...f, compositeSubfamilyId: e.target.value, compositeProductId: '' }))}
                className={`${inputCls} bg-white`}
              >
                <option value="">— Selecciona —</option>
                {compositeSubfamilies
                  .filter((s) => !form.compositeCodeId || String(s.compositeCodeId) === form.compositeCodeId)
                  .map((s) => (
                    <option key={s.id} value={String(s.id)}>{s.code} - {s.name}</option>
                  ))
                }
              </select>
            </div>
            <div>
              <label className={labelCls}>Producte</label>
              <select
                value={form.compositeProductId}
                disabled={!form.compositeSubfamilyId}
                onChange={(e) => setForm((f) => ({ ...f, compositeProductId: e.target.value }))}
                className={`${inputCls} bg-white`}
              >
                <option value="">— Selecciona —</option>
                {compositeProducts
                  .filter((p) => !form.compositeSubfamilyId || String(p.subfamilyId) === form.compositeSubfamilyId)
                  .map((p) => (
                    <option key={p.id} value={String(p.id)}>{p.code} - {p.name}</option>
                  ))
                }
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 xl:grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>Responsable projecte client</label>
              <select
                value={form.responsableProjecte}
                onChange={(e) => setForm((f) => ({ ...f, responsableProjecte: e.target.value }))}
                className={`${inputCls} bg-white`}
              >
                <option value="">— Selecciona un contacte —</option>
                {form.responsableProjecte && !contacts.some((c) => `${c.firstName} ${c.lastName}` === form.responsableProjecte) && (
                  <option value={form.responsableProjecte}>{form.responsableProjecte}</option>
                )}
                {contacts.map((c) => (
                  <option key={c.id} value={`${c.firstName} ${c.lastName}`}>{c.firstName} {c.lastName}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Responsable</label>
              <select
                value={form.responsableEmpresa}
                onChange={(e) => setForm((f) => ({ ...f, responsableEmpresa: e.target.value }))}
                className={`${inputCls} bg-white`}
              >
                <option value="">— Selecciona —</option>
                {form.responsableEmpresa && !companyUsers.some((u) => `${u.firstName} ${u.lastName}` === form.responsableEmpresa) && (
                  <option value={form.responsableEmpresa}>{form.responsableEmpresa}</option>
                )}
                {companyUsers.map((u) => (
                  <option key={u.id} value={`${u.firstName} ${u.lastName}`}>{u.firstName} {u.lastName}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {clientDetail && (
          <div className="bg-white border border-gray-200 rounded-lg">
            <div className="px-5 py-3 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <User size={16} className="text-blue-600" /> Contactes
              </h2>
            </div>
            {contacts.length === 0 ? (
              <p className="px-5 py-6 text-xs text-muted-foreground text-center">
                Aquest client no té contactes registrats.
              </p>
            ) : (
              <div className="divide-y divide-gray-50">
                {contacts.map((c) => (
                  <div key={c.id} className="px-5 py-3">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-medium text-gray-900">{c.firstName} {c.lastName}</span>
                      {c.isPrimary && <Star size={11} className="text-amber-500 fill-amber-500" />}
                    </div>
                    {c.position && <p className="text-[11px] text-muted-foreground">{c.position}</p>}
                    {c.email && (
                      <p className="flex items-center gap-1.5 text-[11px] text-gray-600 mt-1"><Mail size={11} className="text-gray-400 shrink-0" /> {c.email}</p>
                    )}
                    {(c.phone || c.mobile) && (
                      <p className="flex items-center gap-1.5 text-[11px] text-gray-600 mt-1"><Phone size={11} className="text-gray-400 shrink-0" /> {c.phone || c.mobile}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="space-y-6">
        {!isNew && (
          <CollapsibleCard
            title="Seguiment Comercial"
            icon={CalendarClock}
            iconClassName="text-amber-600"
            count={seguiments.length}
            showNewIndicator={hasUnseenSeguiment}
            onToggle={handleSeguimentCardToggle}
            headerActions={(open) => open && !showSeguimentForm && (
              <button
                onClick={handleOpenSeguimentForm}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white rounded text-xs font-medium hover:bg-amber-700"
              >
                <Plus size={14} /> Nou Seguiment
              </button>
            )}
          >
            {showSeguimentForm && (
              <div className="px-5 py-4 border-b border-gray-100 bg-amber-50/40 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Persona que fa el seguiment</label>
                    <select
                      value={seguimentForm.userId}
                      onChange={(e) => setSeguimentForm((f) => ({ ...f, userId: e.target.value }))}
                      className={`${inputCls} bg-white`}
                    >
                      {seguimentUserOptions.map((u) => (
                        <option key={u.id} value={u.id}>{u.firstName} {u.lastName}{u.role ? ` (${u.role})` : ''}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Data del comentari</label>
                    <input
                      type="date"
                      value={seguimentForm.dataComentari}
                      onChange={(e) => setSeguimentForm((f) => ({ ...f, dataComentari: e.target.value }))}
                      className={inputCls}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Canal</label>
                    <select
                      value={seguimentForm.canal}
                      onChange={(e) => setSeguimentForm((f) => ({ ...f, canal: e.target.value }))}
                      className={`${inputCls} bg-white`}
                    >
                      <option value="TELEFONIC">Telefònic</option>
                      <option value="CORREU">Correu</option>
                      <option value="ALTRES">Altres</option>
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Persona interlocutora</label>
                    <select
                      value={seguimentForm.interlocutor}
                      onChange={(e) => setSeguimentForm((f) => ({ ...f, interlocutor: e.target.value }))}
                      className={`${inputCls} bg-white`}
                    >
                      <option value="">— Selecciona —</option>
                      {/* Valor antic (text lliure) no present a la llista de contactes actual
                          — es mostra tal qual en lloc d'amagar-lo. */}
                      {seguimentForm.interlocutor && !contacts.some((c) => `${c.firstName} ${c.lastName}`.trim() === seguimentForm.interlocutor) && (
                        <option value={seguimentForm.interlocutor}>{seguimentForm.interlocutor}</option>
                      )}
                      {contacts.map((c) => (
                        <option key={c.id} value={`${c.firstName} ${c.lastName}`.trim()}>
                          {c.firstName} {c.lastName}{c.position ? ` — ${c.position}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Comentari</label>
                  <textarea
                    value={seguimentForm.comentari}
                    onChange={(e) => setSeguimentForm((f) => ({ ...f, comentari: e.target.value }))}
                    rows={3}
                    className={inputCls}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Data pròxim avís</label>
                    <input
                      type="date"
                      value={seguimentForm.dataProperAvis}
                      onChange={(e) => setSeguimentForm((f) => ({ ...f, dataProperAvis: e.target.value }))}
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Valoració de l'estat</label>
                    <select
                      value={seguimentForm.valoracio}
                      onChange={(e) => setSeguimentForm((f) => ({ ...f, valoracio: e.target.value }))}
                      className={`${inputCls} bg-white`}
                    >
                      <option value="">— Sense valorar —</option>
                      {Object.entries(VALORACIO_LABEL).map(([code, label]) => (
                        <option key={code} value={code}>{label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2 pt-1">
                  <button
                    onClick={() => { setShowSeguimentForm(false); setEditingSeguimentId(null); }}
                    className="px-3 py-1.5 text-xs font-medium text-gray-600 rounded hover:bg-gray-100"
                  >
                    Cancel·lar
                  </button>
                  <button
                    onClick={handleSaveSeguiment}
                    disabled={savingSeguiment}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white rounded text-xs font-medium hover:bg-amber-700 disabled:opacity-50"
                  >
                    <Save size={14} /> {savingSeguiment ? 'Desant...' : (editingSeguimentId ? 'Actualitzar seguiment' : 'Desar seguiment')}
                  </button>
                </div>
              </div>
            )}

            {seguiments.length === 0 ? (
              <p className="px-5 py-6 text-xs text-muted-foreground text-center">
                Encara no hi ha cap seguiment comercial registrat.
              </p>
            ) : (
              <div className="divide-y divide-gray-50">
                {seguiments.map((s) => (
                  <div key={s.id} className="px-5 py-3 group">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
                        <span className="font-medium text-gray-900">{new Date(s.dataComentari).toLocaleDateString('ca-ES')}</span>
                        <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{CANAL_LABEL[s.canal] ?? s.canal}</span>
                        {s.interlocutor && <span>amb <span className="text-gray-700">{s.interlocutor}</span></span>}
                        {s.valoracio && (
                          <span className={`px-1.5 py-0.5 rounded font-medium ${VALORACIO_COLOR[s.valoracio] ?? 'bg-gray-100 text-gray-600'}`}>
                            {VALORACIO_LABEL[s.valoracio] ?? s.valoracio}
                          </span>
                        )}
                      </div>
                      <div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5 shrink-0">
                      <button
                        onClick={() => handleEditSeguiment(s)}
                        className="p-1 rounded hover:bg-blue-50 text-gray-400 hover:text-blue-600"
                        title="Editar"
                      >
                        <Pencil size={13} />
                      </button>
                      <button
                        onClick={() => handleDeleteSeguiment(s.id)}
                        className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-600"
                        title="Eliminar"
                      >
                        <Trash2 size={13} />
                      </button>
                      </div>
                    </div>
                    <p className="text-xs text-gray-700 mt-1 whitespace-pre-wrap">{s.comentari}</p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[11px] text-muted-foreground">
                      {s.user && <span>Registrat per {s.user.firstName} {s.user.lastName}</span>}
                      {s.dataProperAvis && (
                        <span className="flex items-center gap-1 text-amber-700">
                          <CalendarClock size={11} /> Pròxim avís: {new Date(s.dataProperAvis).toLocaleDateString('ca-ES')}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CollapsibleCard>
        )}

        {!isNew && tender && (
          <CollapsibleCard title="Licitació" icon={Gavel} iconClassName="text-blue-600">
            <div className="p-5 grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div className="md:col-span-3">
                <p className="text-xs font-medium text-muted-foreground mb-1">Objecte</p>
                <p>{tender.location || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Població</p>
                <p>{tender.poblacio || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Import Total</p>
                <p>{tender.totalAmount != null ? Number(tender.totalAmount).toLocaleString('ca-ES', { style: 'currency', currency: 'EUR' }) : '—'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Import Adjudicació</p>
                <p>{tender.adjudicationAmount != null ? Number(tender.adjudicationAmount).toLocaleString('ca-ES', { style: 'currency', currency: 'EUR' }) : '—'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Ràtio Adjudicació/Total</p>
                <p>{tender.adjudicationAmount != null && tender.totalAmount ? `${(tender.adjudicationAmount / tender.totalAmount * 100).toLocaleString('ca-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %` : '—'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Tipus</p>
                <p>{tender.tenderType === 'PUBLIC' ? 'Públic' : tender.tenderType === 'PRIVAT' ? 'Privat' : '—'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Data Avís</p>
                <p>{tender.noticeDate ? new Date(tender.noticeDate).toLocaleDateString('ca-ES') : '—'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Venciment</p>
                <p>{tender.deadline ? new Date(tender.deadline).toLocaleString('ca-ES') : '—'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Estat Licitació</p>
                <p>{TENDER_STATUS_LABEL[tender.status] ?? tender.status}</p>
              </div>
              <div className="md:col-span-3">
                <a href={`/crm/licitacions/${tender.id}/edit`} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-blue-600 hover:underline text-xs">
                  <ExternalLink className="h-3 w-3" /> Obrir la licitació
                </a>
              </div>
            </div>
          </CollapsibleCard>
        )}

        {!isNew && (
          <CollapsibleCard
            title="Pressupostos"
            icon={ClipboardList}
            iconClassName="text-purple-600"
            count={budgets.length}
            headerActions={(open) => open && EC_ESTATS_PERMETEN_NOU_PRESSUPOST.includes(estat) && (
              <button
                onClick={() => navigate(`/crm/pressupostos/new?ecId=${id}`)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded text-xs font-medium hover:bg-purple-700"
              >
                <Plus size={14} /> Nou Pressupost
              </button>
            )}
          >
            {budgets.length === 0 ? (
              <p className="px-5 py-6 text-xs text-muted-foreground text-center">
                Aquest expedient encara no té cap pressupost.
              </p>
            ) : (
              <div className="overflow-x-auto">
              <table className="w-full text-xs text-muted-foreground">
                <thead>
                  <tr className="text-left text-muted-foreground border-b border-gray-100">
                    <th className="px-5 py-2 font-medium">Número</th>
                    <th className="px-5 py-2 font-medium">Projecte</th>
                    <th className="px-5 py-2 font-medium">Estat</th>
                    <th className="px-5 py-2 font-medium text-right">Import</th>
                  </tr>
                </thead>
                <tbody>
                  {budgets.map((b) => (
                    <tr
                      key={b.id}
                      className="border-b border-gray-50 last:border-0 hover:bg-gray-50 cursor-pointer"
                      onClick={() => window.open(`/crm/pressupostos/${b.id}/edit`, '_blank', 'noopener,noreferrer')}
                    >
                      <td className="px-5 py-2 font-medium">{b.budgetNumber}</td>
                      <td className="px-5 py-2">{b.projecte ?? '—'}</td>
                      <td className="px-5 py-2">{ESTAT_LABEL[b.estat] ?? b.estat}</td>
                      <td className="px-5 py-2 text-right">
                        {Number(b.baseImposable || 0).toLocaleString('ca-ES', { style: 'currency', currency: 'EUR' })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </CollapsibleCard>
        )}

        {!isNew && (
          <CollapsibleCard title="Comandes" icon={ShoppingCart} iconClassName="text-green-600">
            <p className="px-5 py-6 text-xs text-muted-foreground text-center">
              Encara no hi ha comandes per aquest expedient.
            </p>
          </CollapsibleCard>
        )}

        {!isNew && (
          <CollapsibleCard title="Albarans" icon={Truck} iconClassName="text-orange-600">
            <p className="px-5 py-6 text-xs text-muted-foreground text-center">
              Encara no hi ha albarans per aquest expedient.
            </p>
          </CollapsibleCard>
        )}

        {!isNew && (
          <CollapsibleCard title="Factures" icon={FileText} iconClassName="text-teal-600">
            <p className="px-5 py-6 text-xs text-muted-foreground text-center">
              Encara no hi ha factures per aquest expedient.
            </p>
          </CollapsibleCard>
        )}

        {!isNew && (
          <CollapsibleCard
            title="Documents Expedient Comercial"
            icon={Paperclip}
            iconClassName="text-amber-600"
            count={documents.length}
          >
            <div className="p-5">
              <label
                onDragOver={(e) => { e.preventDefault(); setDocsDragOver(true); }}
                onDragLeave={() => setDocsDragOver(false)}
                onDrop={handleDocumentsDrop}
                className={`flex flex-col items-center justify-center gap-1.5 border-2 border-dashed rounded-lg py-4 cursor-pointer transition-colors ${
                  docsDragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <Upload size={18} className="text-gray-400" />
                <span className="text-xs text-muted-foreground">
                  {uploadingDoc ? 'Pujant...' : 'Arrossega o clica per pujar un document'}
                </span>
                <input
                  type="file"
                  className="hidden"
                  disabled={uploadingDoc}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleUploadDocument(file);
                    e.target.value = '';
                  }}
                />
              </label>

              {documents.length === 0 ? (
                <p className="text-muted-foreground text-xs text-center py-3">Encara no hi ha cap document</p>
              ) : (
                <div className="mt-3 space-y-1">
                  {documents.map((doc) => (
                    <div key={doc.id} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-gray-50 text-xs">
                      <FileText size={14} className="text-gray-400 shrink-0" />
                      <div className="w-80 shrink-0 min-w-0">
                        <div className="truncate font-medium" title={doc.name}>{doc.name}</div>
                        <div className="text-muted-foreground">{fmtFileSize(doc.fileSize)} · {new Date(doc.createdAt).toLocaleDateString('ca-ES')}</div>
                      </div>
                      <div className="flex-1 min-w-0 flex items-center">
                        {editingDocId === doc.id ? (
                          <input
                            type="text"
                            autoFocus
                            value={editingDocDescription}
                            onChange={(e) => setEditingDocDescription(e.target.value)}
                            onBlur={() => handleSaveDocDescription(doc.id)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveDocDescription(doc.id);
                              if (e.key === 'Escape') setEditingDocId(null);
                            }}
                            placeholder="Descripció del document"
                            className="w-full px-1.5 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-primary text-xs"
                          />
                        ) : doc.description ? (
                          <div className="truncate text-muted-foreground" title={doc.description}>{doc.description}</div>
                        ) : null}
                      </div>
                      <button type="button" title="Editar descripció" onClick={() => startEditDocDescription(doc)}
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-amber-600 transition-colors">
                        <Pencil size={14} />
                      </button>
                      <button type="button" title="Visualitzar" onClick={() => handleOpenDocument(doc, false)}
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-blue-600 transition-colors">
                        <Eye size={14} />
                      </button>
                      <button type="button" title="Descarregar" onClick={() => handleOpenDocument(doc, true)}
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-green-600 transition-colors">
                        <Download size={14} />
                      </button>
                      <button type="button" title="Eliminar" onClick={() => handleDeleteDocument(doc.id)}
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-red-600 transition-colors">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CollapsibleCard>
        )}
      </div>

      </div>
      </div>
    </div>
  );
}
